"use client"

import {useUiControlLabels} from "@/components/i18n/ui-control-labels-context"
import { useCallback, useEffect, useState } from "react"
import { AlertTriangle, RefreshCw } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { useAuth } from "@/hooks/use-auth"
import type { LifecycleJobFailure } from "@/app/api/tenants/lifecycle-jobs/route"
import { useTranslations } from "next-intl"


const JOB_TYPE_LABELS: Record<string, string> = {
  tenant_archive: "Archive",
  tenant_restore: "Restore",
  tenant_purge: "Purge",
}

const formatDate = (iso: string, dateLocale: string = "en-US") =>
  new Date(iso).toLocaleString(dateLocale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })

/**
 * Super-user banner surfacing currently-failed tenant lifecycle jobs
 * (archive / restore / purge) from `background_jobs`. The daily purge cron
 * runs unattended, so without this a failed purge only lives in the DB. Renders
 * nothing when there are no failures (or on fetch error — non-blocking).
 */
export function LifecycleJobsAlert() {
  const {locale: uiDateLocale} = useUiControlLabels()

  const tUi = useTranslations("TenantManagement.TenantManagementLifecycleJobsAlert")

  const { fetchWithAuth } = useAuth()
  const [failures, setFailures] = useState<LifecycleJobFailure[]>([])
  const [isChecking, setIsChecking] = useState(false)

  const load = useCallback(async () => {
    setIsChecking(true)
    try {
      const res = await fetchWithAuth("/api/tenants/lifecycle-jobs")
      if (!res.ok) return
      const data = await res.json()
      setFailures(Array.isArray(data.failures) ? data.failures : [])
    } catch {
      // Observability is best-effort — never block the page on it.
    } finally {
      setIsChecking(false)
    }
  }, [fetchWithAuth])

  useEffect(() => {
    load()
  }, [load])

  if (failures.length === 0) return null

  return (
    <Card className="border-destructive/50 bg-destructive/5">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-4">
          <CardTitle className="flex items-center gap-2 text-base text-destructive">
            <AlertTriangle className="w-5 h-5" />
            {failures.length} <span><span key={tUi("failedTenantLifecycleJob")}>{tUi("failedTenantLifecycleJob")}</span></span><span>{failures.length === 1 ? "" : "s"}</span>
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={load} disabled={isChecking}>
            <RefreshCw className={`w-4 h-4 mr-2 ${isChecking ? "animate-spin" : ""}`} />
            <span><span key={tUi("recheck")}>{tUi("recheck")}</span></span>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {failures.map((f) => (
          <div key={f.id} className="rounded-md border border-destructive/30 bg-background/50 p-3 text-sm">
            <div className="flex items-center gap-2">
              <Badge variant="outline">{JOB_TYPE_LABELS[f.job_type] ?? f.job_type}</Badge>
              <span className="font-medium">
                {f.tenant_name ?? f.tenant_id?.slice(0, 8) ?? tUi("unknownTenant")}
              </span>
              <span className="text-xs text-muted-foreground ml-auto">{formatDate(f.failed_at, uiDateLocale)}</span>
            </div>
            {f.last_error && (
              <p className="mt-1.5 font-mono text-xs text-destructive break-all">{f.last_error}</p>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  )
}
